'use client';
import { FC, useContext } from 'react';
import { styled, Container, Box, useTheme } from '@mui/material';
import { Outlet } from 'react-router';
import Header from './vertical/header/Header';
import Sidebar from './vertical/sidebar/Sidebar';
import Navigation from '../full/horizontal/navbar/Navigation';
import HorizontalHeader from '../full/horizontal/header/Header';
import ScrollToTop from '../../components/shared/ScrollToTop';
import { CustomizerContext } from '@/context/CustomizerContext';
import config from '@/context/config';

const MainWrapper = styled('div')(() => ({
  display: 'flex',
  minHeight: '100vh',
  width: '100%',
}));

const PageWrapper = styled('div')(() => ({
  display: 'flex',
  flexGrow: 1,
  paddingBottom: '60px',
  flexDirection: 'column',
  zIndex: 1,
  width: '100%',
  backgroundColor: 'transparent',
}));

const FullLayout: FC = () => {
  const { activeLayout, activeMode, isCollapse } =
    useContext(CustomizerContext);
  const theme = useTheme();
  const MiniSidebarWidth = config.miniSidebarWidth;

  return (
    <>
      <MainWrapper
        className={activeMode === 'dark' ? 'darkbg mainwrapper' : 'mainwrapper'}
      >
        {/* ------------------------------------------- */}
        {/* Sidebar */}
        {/* ------------------------------------------- */}
        {activeLayout === 'horizontal' ? '' : <Sidebar />}
        {/* ------------------------------------------- */}
        {/* Main Wrapper */}
        {/* ------------------------------------------- */}
        <PageWrapper
          className="page-wrapper"
          sx={{
            position: 'relative',
            ...(isCollapse === 'mini-sidebar' && {
              [theme.breakpoints.up('lg')]: { ml: `${MiniSidebarWidth}px` },
              maxWidth: '100vw',
            }),
          }}
        >
          {/* Background SVG Icons - 2 Large X-shaped stars randomly placed */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            {/* First large X star */}
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 240 240"
              sx={{
                position: 'absolute',
                top: '0%',
                left: '0%',
                width: { xs: '70vw', sm: '60vw', md: '50vw' },
                height: 'auto',
                maxWidth: '800px',
                transform: 'rotate(5deg)',
              }}
            >
              <path
                d="M32 24 H88 L120 80 L152 24 H208 L152 120 L208 216 H152 L120 160 L88 216 H32 L88 120 Z"
                fill="none"
                stroke={theme.palette.mode === 'dark' ? '#3f3f3f' : '#f2f2f0'}
                strokeWidth="4"
              />
            </Box>
            {/* Second large X star */}
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 240 240"
              sx={{
                position: 'absolute',
                bottom: '0%',
                right: '-10%',
                width: { xs: '35vw', sm: '35vw', md: '35vw' },
                height: 'auto',
                maxWidth: '750px',
                transform: 'rotate(-5deg)',
              }}
            >
              <path
                d="M40 30 L90 30 L120 80 L150 30 L200 30 L150 120 L200 210 L150 210 L120 160 L90 210 L40 210 L90 120 Z"
                fill="none"
                stroke={theme.palette.mode === 'dark' ? '#3f3f3f' : '#f2f2f0'}
                strokeWidth="4"
              />
            </Box>
          </Box>

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* ------------------------------------------- */}
            {/* Header */}
            {/* ------------------------------------------- */}
            {activeLayout === 'horizontal' ? <HorizontalHeader /> : <Header />}
            {/* PageContent */}
            {activeLayout === 'horizontal' ? <Navigation /> : ''}
            <Container
              sx={{
                pt: '30px',
                maxWidth: '100%!important',
              }}
            >
              {/* ------------------------------------------- */}
              {/* PageContent */}
              {/* ------------------------------------------- */}

              <Box sx={{ minHeight: 'calc(100vh - 170px)' }}>
                <ScrollToTop>
                  <Outlet />
                </ScrollToTop>
              </Box>

              {/* ------------------------------------------- */}
              {/* End Page */}
              {/* ------------------------------------------- */}
            </Container>
          </Box>
        </PageWrapper>
      </MainWrapper>
    </>
  );
};

export default FullLayout;
